// Versión de asesoras de "Consultar estudiante". El componente compartido
// (components/searchStudent) lo siguen usando admin y profesores sin cambios.
import React from 'react';
import {
  Button,
  Flex,
  FormControl,
  FormLabel,
  Grid,
  Input,
  Spinner,
  Table,
  TableContainer,
  Tbody,
  Td,
  Text,
  Th,
  Thead,
  Tr,
} from '@chakra-ui/react';
import { Link } from 'react-router-dom';
import dayjs from 'dayjs';

import xhr from 'xhr';
import AiPagination from 'components/monitoring/ai/AiPagination';
import { BRAND, EmptyState, PageHeader, SectionCard } from './components/ui';

const fullName = (user) => `${user?.first_name || ''} ${user?.last_name || ''}`.trim();

const EMPTY = { id: '', email: '', cellphone: '', firstName: '', lastName: '' };

const PAGE_SIZE = 20;

// Mismo criterio que el buscador compartido (components/searchStudent): se usa
// el primer dato lleno en este orden: código, correo, celular, nombres/apellidos.
const buildFilter = ({ id, email, cellphone, firstName, lastName }) => {
  const enc = (value) => encodeURIComponent(String(value).trim());
  if (String(id).trim()) {
    // Un número que no cabe en un entero de Postgres no puede ser un código.
    const code = parseInt(id, 10);
    return `filters[id][$eq]=${code > 0 && code <= 2147483647 ? code : 0}`;
  }
  if (email.trim()) return `filters[user][email][$containsi]=${enc(email)}`;
  if (cellphone.trim()) return `filters[user][cellphone][$containsi]=${enc(cellphone)}`;
  const parts = [];
  if (firstName.trim()) parts.push(`filters[user][first_name][$containsi]=${enc(firstName)}`);
  if (lastName.trim()) parts.push(`filters[user][last_name][$containsi]=${enc(lastName)}`);
  return parts.join('&');
};

export default function SearchStudentAdviser() {
  const [students, setStudents] = React.useState(null);
  const [fetching, setFetching] = React.useState(false);
  const [values, setValues] = React.useState(EMPTY);
  const [query, setQuery] = React.useState('');
  const [pagination, setPagination] = React.useState({ page: 1, pageSize: PAGE_SIZE, total: 0, pageCount: 0 });

  // Sin criterios se listan los estudiantes más recientes.
  const load = async (filter, page = 1) => {
    try {
      setFetching(true);
      const res = await xhr(
        `/students?${filter ? `${filter}&` : ''}pagination[page]=${page}&pagination[pageSize]=${PAGE_SIZE}` +
          `&sort[0]=id:desc&populate[user]=true&populate[adviser][populate]=user`
      );
      setStudents(res.data.data);
      const meta = res.data.meta?.pagination || {};
      setPagination({ page, pageSize: PAGE_SIZE, total: meta.total || 0, pageCount: meta.pageCount || 0 });
    } catch (error) {
      console.error(error);
      setStudents([]);
      setPagination({ page: 1, pageSize: PAGE_SIZE, total: 0, pageCount: 0 });
    } finally {
      setFetching(false);
    }
  };

  React.useEffect(() => {
    load('');
  }, []);

  const hasCriteria = Object.values(values).some((value) => String(value).trim());

  const runSearch = () => {
    if (!hasCriteria) return;
    const filter = buildFilter(values);
    setQuery(filter);
    load(filter, 1);
  };

  const clear = () => {
    setValues(EMPTY);
    setQuery('');
    load('', 1);
  };

  const field = (name, label, placeholder) => (
    <FormControl>
      <FormLabel fontSize="sm" color="gray.700">
        {label}
      </FormLabel>
      <Input
        id={`search-${name}`}
        placeholder={placeholder}
        value={values[name]}
        onChange={(e) => setValues({ ...values, [name]: e.target.value })}
        onKeyDown={(e) => {
          if (e.key === 'Enter') runSearch();
        }}
      />
    </FormControl>
  );

  const list = (students || []).filter((el) => el.user);

  return (
    <>
      <PageHeader title="Consultar estudiante" subtitle="Busca por código, correo, celular o nombre." />

      <SectionCard mb={4} padding={4}>
        <Grid templateColumns={{ base: '1fr', md: 'repeat(2, 1fr)', xl: 'repeat(5, 1fr)' }} gap={3}>
          {field('id', 'Código', 'Ej.: 22941')}
          {field('email', 'Correo', 'correo@ejemplo.com')}
          {field('cellphone', 'Celular', 'Número')}
          {field('firstName', 'Nombres', 'Nombres')}
          {field('lastName', 'Apellidos', 'Apellidos')}
        </Grid>
        <Flex justify="space-between" align="center" mt={4} gap={3} wrap="wrap">
          <Text fontSize="xs" color="gray.500">
            Se usa el primer dato lleno, en este orden: código, correo, celular, nombres y apellidos.
          </Text>
          <Flex gap={3}>
            <Button variant="ghost" onClick={clear}>
              Limpiar
            </Button>
            <Button
              bg={BRAND}
              color="white"
              _hover={{ bg: '#463fd1' }}
              onClick={runSearch}
              isLoading={fetching}
              isDisabled={!hasCriteria}
            >
              Buscar
            </Button>
          </Flex>
        </Flex>
      </SectionCard>

      <SectionCard padding={0} overflow="hidden">
        <Flex px={5} pt={4} pb={2}>
          <Text fontSize="sm" color="gray.500">
            {query ? 'Resultados de la búsqueda' : 'Estudiantes, del más reciente al más antiguo'}
          </Text>
        </Flex>
        {fetching || students === null ? (
          <Flex justify="center" py={12}>
            <Spinner color={BRAND} />
          </Flex>
        ) : list.length === 0 ? (
          <EmptyState title="No hay estudiantes para tu búsqueda" description="Revisa el dato o prueba con otro criterio." />
        ) : (
          <TableContainer>
            <Table>
              <Thead bg="gray.50">
                <Tr>
                  <Th>Estudiante</Th>
                  <Th>Contacto</Th>
                  <Th>Asesora</Th>
                  <Th>Registro</Th>
                  <Th textAlign="right">Detalle</Th>
                </Tr>
              </Thead>
              <Tbody>
                {list.map((el) => (
                  <Tr key={el.id} _hover={{ bg: 'gray.50' }}>
                    <Td>
                      <Text fontWeight="600" color="gray.800" whiteSpace="normal">
                        {fullName(el.user) || 'Sin nombre'}
                      </Text>
                      <Text fontSize="xs" color="gray.500">
                        Código {el.id}
                      </Text>
                    </Td>
                    <Td>
                      <Text fontSize="sm">{el.user.email}</Text>
                      <Text fontSize="xs" color="gray.500">
                        {el.user.cellphone}
                      </Text>
                    </Td>
                    <Td fontSize="sm">{fullName(el.adviser?.user) || '—'}</Td>
                    <Td fontSize="sm">{dayjs(el.createdAt).format('DD/MM/YYYY')}</Td>
                    <Td textAlign="right">
                      <Button
                        as={Link}
                        to={`/plataforma/asesoras/ver-estudiante/${el.id}`}
                        size="sm"
                        variant="outline"
                        colorScheme="purple"
                      >
                        Ver
                      </Button>
                    </Td>
                  </Tr>
                ))}
              </Tbody>
            </Table>
          </TableContainer>
        )}
        {!fetching && (
          <AiPagination pagination={pagination} noun="estudiantes" onPageChange={(page) => load(query, page)} />
        )}
      </SectionCard>
    </>
  );
}
